// @ts-nocheck
import { supabase } from '@/integrations/supabase/client';

// Get all active scoring configs, optionally filtered
export async function getScoringConfigs(filters?: { 
  event_type?: string; 
  age_group?: string; 
  category?: string 
}) {
  let query = supabase
    .from('rlhoops_scoring_configs')
    .select('*')
    .eq('is_active', true)
    .order('category')
    .order('age_group')
    .order('event_type');
  
  if (filters?.event_type) query = query.eq('event_type', filters.event_type);
  if (filters?.age_group) query = query.eq('age_group', filters.age_group);
  if (filters?.category) query = query.eq('category', filters.category);
  
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

// Calculate points for a game result
export function calculatePoints(
  config: any, 
  result: 'win' | 'loss', 
  pointDifferential: number, 
  opponentIsShoeTeam: boolean
): number {
  // Blowout check: if threshold exists and loss margin >= threshold, 0 points
  if (result === 'loss' && config.blowout_threshold && pointDifferential >= config.blowout_threshold) {
    return config.blowout_points || 0;
  }
  
  if (result === 'win') {
    return Number(config.win_points);
  }
  
  // Loss to shoe team gets bonus points (if configured)
  if (result === 'loss' && opponentIsShoeTeam && config.shoe_team_loss_points != null) {
    return Number(config.shoe_team_loss_points);
  }
  
  return Number(config.loss_points);
}

// Add a manual ranking point entry
export async function addManualPoints(entry: {
  team_id: string;
  team_name: string;
  points_earned: number;
  result: 'win' | 'loss';
  point_differential: number;
  opponent_is_shoe_team: boolean;
  scoring_config_id: string;
  age_group: string;
  season?: string;
  entered_by?: string;
  tournament_id?: string;
}) {
  const { data, error } = await supabase
    .from('rlhoops_ranking_points')
    .insert({
      ...entry,
      season: entry.season || '2025-2026',
      is_manual_entry: true,
    })
    .select()
    .single();
  if (error) throw error;
  
  // After inserting, recalculate team ranking
  await recalculateTeamRanking(entry.team_id, entry.team_name, entry.age_group, entry.season || '2025-2026');
  
  return data;
}

// Delete a ranking point entry
export async function deletePointEntry(entryId: string) {
  // Get entry details first for recalculation
  const { data: entry } = await supabase
    .from('rlhoops_ranking_points')
    .select('team_id, team_name, age_group, season')
    .eq('id', entryId)
    .single();
  
  const { error } = await supabase
    .from('rlhoops_ranking_points')
    .delete()
    .eq('id', entryId);
  if (error) throw error;
  
  // Recalculate after deletion
  if (entry) {
    await recalculateTeamRanking(entry.team_id, entry.team_name, entry.age_group, entry.season);
  }
}

// Get all point entries for a team
export async function getTeamPointHistory(teamId: string, season?: string) {
  let query = supabase
    .from('rlhoops_ranking_points')
    .select('*, rlhoops_scoring_configs(config_name)')
    .eq('team_id', teamId)
    .order('created_at', { ascending: false });
  
  if (season) query = query.eq('season', season);
  
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

// Get leaderboard/rankings
export async function getTeamRankings(filters: { 
  age_group: string; 
  season?: string;
  category?: string;
}) {
  let query = supabase
    .from('rlhoops_team_rankings')
    .select('*')
    .eq('age_group', filters.age_group)
    .eq('season', filters.season || '2025-2026')
    .order('total_points', { ascending: false });
  
  if (filters.category) query = query.eq('category', filters.category);
  
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

// Recalculate a team's total ranking from all their point entries
async function recalculateTeamRanking(teamId: string, teamName: string, ageGroup: string, season: string) {
  const { data: entries } = await supabase
    .from('rlhoops_ranking_points')
    .select('points_earned, result')
    .eq('team_id', teamId)
    .eq('age_group', ageGroup)
    .eq('season', season);
  
  const totalPoints = (entries || []).reduce((sum, e) => sum + Number(e.points_earned), 0);
  const totalWins = (entries || []).filter(e => e.result === 'win').length;
  const totalLosses = (entries || []).filter(e => e.result === 'loss').length;
  const gamesPlayed = (entries || []).length;
  
  const category = ['14u', '13u', '12u', '11u'].includes(ageGroup) ? 'youth' : 'high_school';
  
  const { error } = await supabase
    .from('rlhoops_team_rankings')
    .upsert({
      team_id: teamId,
      team_name: teamName,
      season,
      age_group: ageGroup,
      category,
      total_points: totalPoints,
      total_wins: totalWins,
      total_losses: totalLosses,
      games_played: gamesPlayed,
      last_updated: new Date().toISOString(),
    }, {
      onConflict: 'team_id,season,age_group'
    });
  if (error) throw error;
}
